import { TaggedLogger, type Logger } from '@core/logging/logger'
import { Result } from '@core/models/result'
import {
    AbstractDataStore,
    DataStoreName,
    StorageType,
    type DataStoreGetResult,
    type DataStoreRecord,
    type SynchronousDataStore,
    type TimestampedDataValue,
} from '@core/storage/abstract-data-store.ts'
import { PNException } from '@core/exceptions/pn-exception.ts'
import { PNExceptionCode } from '@core/exceptions/pn-exception-code.ts'

const MemDataError = {
    notAvailable: () => new PNException(PNExceptionCode.STORAGE_NOT_AVAILABLE, null, 'Mem'),
    noStoreName: () => new PNException(PNExceptionCode.STORAGE_NS_REQUIRED, null, 'Mem'),
    noDataFor: (key: string) => new PNException(PNExceptionCode.STORAGE_KEY_EMPTY, null, key),
    unableToOpen: (reason: Error) => new PNException(PNExceptionCode.STORAGE_FAILED_TO_OPEN, reason, `Mem`),
} as const

type MemStoreDb = Record<DataStoreName, Record<string, TimestampedDataValue>>

/**
 * Holds its records in a plain object, so every operation is already synchronous and the async
 * methods only wrap the synchronous ones.
 *
 * Declaring that is what lets the synchronous storage paths use it. Those paths keep only the
 * stores implementing this interface, and a cookie was the sole one: a key the manager deliberately
 * keeps out of the cookie - anything an origin may hold but not publish to the rest of the domain -
 * had nowhere else to go, so the write selected no store at all and the read could never see it.
 */
export class MemDataStore extends AbstractDataStore implements SynchronousDataStore {
    protected readonly _db: MemStoreDb

    public constructor(storeName: DataStoreName | null, logger: Logger = TaggedLogger._getInstance('MemStore')) {
        super(StorageType.MEM, storeName, logger)

        this._db = {} as MemStoreDb
    }

    public get _isAvailable(): boolean {
        return this._storeName !== null
    }

    public async _getData<T>(key: string): Promise<DataStoreGetResult<T>> {
        return this._getDataSync<T>(key)
    }

    public async _setData<T>(key: string, value: T): Promise<Result<T>> {
        return this._setDataSync<T>(key, value)
    }

    public async _removeData(key: string): Promise<Result<null>> {
        return this._removeDataSync(key)
    }

    public _getDataSync<T>(key: string): DataStoreGetResult<T> {
        if (!this._storeName) {
            return Result.Err(MemDataError.noStoreName())
        }

        const record = this._db[this._storeName]?.[key]
        return Result.Ok<DataStoreRecord<T>>({
            value: record?.value ?? null,
            ts: record?.ts ?? 0,
            sourceType: this._storageType,
        })
    }

    public _setDataSync<T>(key: string, value: T): Result<T> {
        if (!this._storeName) {
            return Result.Err(MemDataError.noStoreName())
        }

        this._db[this._storeName] ??= {}
        this._db[this._storeName][key] = { value, ts: new Date().getTime() }
        return Result.Ok(value)
    }

    public _removeDataSync(key: string): Result<null> {
        if (!this._storeName) {
            return Result.Err(MemDataError.noStoreName())
        }

        if (this._db[this._storeName]?.[key] !== undefined) {
            delete this._db[this._storeName][key]
        }

        return Result.Ok(null)
    }
}
